import React, { useEffect, useRef, useState } from "react";
import { FaCheck, FaCopy } from "react-icons/fa";
import "./CopyableId.css";

// A record's id, shown in full, copied to the clipboard on click.
//
// Props
//   id      the id to show and copy (nothing renders without one)
//   label   the word before it, "ID" by default
//
// navigator.clipboard needs a secure page and permission; where it is refused,
// a hidden textarea and execCommand("copy") do the same job.
const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_) {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const done = document.execCommand("copy");
    area.remove();
    return done;
  }
};

const CopyableId = ({ id, label = "ID" }) => {
  const [state, setState] = useState("idle");        // "idle" | "copied" | "failed"
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);
  // A different record starts fresh, not showing the last one's "Copied".
  useEffect(() => { setState("idle"); }, [id]);

  if (!id) return null;
  const value = String(id);

  const copy = async (e) => {
    e.stopPropagation();
    const done = await copyText(value);
    setState(done ? "copied" : "failed");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState("idle"), 1500);
  };

  return (
    <button
      type="button"
      className={`copyableId${state === "copied" ? " copyableIdCopied" : ""}`}
      onClick={copy}
      title={state === "failed" ? "Could not copy — select the ID and copy it by hand" : "Click to copy"}
      aria-label={`${label} ${value}. Click to copy`}
    >
      <span className="copyableIdLabel">{label}</span>
      <span className="copyableIdValue">{value}</span>
      <span className="copyableIdIcon" aria-live="polite">
        {state === "copied" ? <><FaCheck /> Copied</> : state === "failed" ? "Copy failed" : <FaCopy />}
      </span>
    </button>
  );
};

export default CopyableId;
